/**
 * Dual Authentication Service
 * Manages authentication with both Stytch and Xano
 */

import { stytchService } from './stytchService';
import { xanoAuthService } from '../../api/services/xanoAuth';
import type { User } from '../../api/auth/schemas/auth';
import type { DualAuthSession } from '../../stytch';
import { getRoleFromId, USER_ROLES, XANO_API_GROUPS, XANO_HOST } from '../../shared/constants';

class DualAuthService {
  /**
   * Send magic link for login
   */
  async sendMagicLink(email: string): Promise<void> {
    try {
      // Check if email is allowed for signup (basic domain check)
      if (!stytchService.isEmailAllowedForSignup(email)) {
        throw new Error('Email domain not allowed for signup. Please contact admin.');
      }
      
      // Send magic link with loginOrCreate (true) - this ensures:
      // 1. If user exists in Stytch: sends login link
      // 2. If user doesn't exist in Stytch: creates account and sends signup link
      // This guarantees users always exist in Stytch before reaching Xano
      await stytchService.sendMagicLink(email, true);
    } catch (error: any) {
      console.error('Failed to send magic link:', error);
      throw new Error(error.message || 'Failed to send magic link');
    }
  }
  
  /**
   * Authenticate with magic link token
   */
  async authenticateWithMagicLink(token: string): Promise<{
    user: User;
    session: DualAuthSession;
  }> {
    try {
      // Step 1: Authenticate magic link with Stytch
      console.log('DualAuthService: Authenticating magic link with Stytch...');
      const stytchResponse = await stytchService.authenticateMagicLink(token);
      console.log('DualAuthService: Stytch authentication successful');
      
      // Step 1.5: Update Stytch user name if this is a signup
      const pendingSignup = sessionStorage.getItem('pendingSignup');
      if (pendingSignup) {
        try {
          const signupData = JSON.parse(pendingSignup);
          if (signupData.email === stytchResponse.user.emails[0].email) {
            console.log('Updating Stytch user name for new signup');
            await stytchService.updateUserName(signupData.firstName, signupData.lastName);
          }
        } catch (error) {
          console.warn('Failed to update Stytch user name:', error);
        }
      }
      
      // Step 2: Exchange for Xano token
      let xanoToken: string | null = null;
      let xanoUser: any = null;
      
      try {
        const exchangeResult = await this.exchangeStytchSessionForXanoToken(stytchResponse);
        xanoToken = exchangeResult.token;
        xanoUser = exchangeResult.user;
        console.log('Using Xano user from exchange response:', xanoUser);
      } catch (error: any) {
        console.error('Failed to exchange Stytch session for Xano token:', error.message);
        throw new Error('Authentication failed. Please ensure backend is properly configured.');
      }
      
      // Step 4: Convert to our User type
      const user = this.mapXanoUserToUser(xanoUser, stytchResponse.user);
      
      // Step 5: Create dual session
      const session: DualAuthSession = {
        stytchSession: {
          session_id: stytchResponse.session.session_id,
          user_id: stytchResponse.user.user_id,
          expires_at: stytchResponse.session.expires_at,
          session_token: stytchResponse.session_token,
          session_jwt: stytchResponse.session_jwt,
        },
        xanoToken: xanoToken || '',
        isAuthenticated: true,
        isLoading: false,
      };
      
      // Store tokens
      this.storeSession(session);
      
      return { user, session };
    } catch (error: any) {
      console.error('Magic link authentication failed:', error);
      throw new Error(error.message || 'Failed to authenticate');
    }
  }
  
  /**
   * Authenticate with OAuth
   */
  async authenticateWithOAuth(token: string): Promise<{
    user: User;
    session: DualAuthSession;
  }> {
    try {
      // Step 1: Authenticate OAuth with Stytch
      const stytchResponse = await stytchService.authenticateOAuth(token);

      // Step 2: Exchange for Xano token and get user
      const exchangeResult = await this.exchangeStytchSessionForXanoToken(stytchResponse);
      const xanoToken = exchangeResult.token;
      const xanoUser = exchangeResult.user;

      // Step 4: Convert to our User type
      const user = this.mapXanoUserToUser(xanoUser, stytchResponse.user);

      // Step 5: Create dual session
      const session: DualAuthSession = {
        stytchSession: {
          session_id: stytchResponse.session.session_id,
          user_id: stytchResponse.user.user_id,
          expires_at: stytchResponse.session.expires_at,
          session_token: stytchResponse.session_token,
          session_jwt: stytchResponse.session_jwt,
        },
        xanoToken: xanoToken || '',
        isAuthenticated: true,
        isLoading: false,
      };

      // Store tokens
      this.storeSession(session);

      return { user, session };
    } catch (error: any) {
      console.error('OAuth authentication failed:', error);
      throw new Error(error.message || 'Failed to authenticate');
    }
  }

  /**
   * Authenticate with SSO (session JWT from Aksanio)
   * NOTE: This method is not used - SSO goes directly through Xano's sso-verify endpoint.
   * See SSOCallback.tsx for the actual SSO flow.
   * @deprecated Use Xano's sso-verify endpoint instead (see SSOCallback.tsx)
   */
  async authenticateWithSSO(_sessionJWT: string): Promise<never> {
    throw new Error(
      'authenticateWithSSO is not used. SSO authentication goes directly through Xano sso-verify endpoint. See SSOCallback.tsx'
    );
  }
  
  /**
   * Exchange Stytch session for Xano token and user
   */
  private async exchangeStytchSessionForXanoToken(
    stytchResponse: any,
    isSSO: boolean = false
  ): Promise<{ token: string; user: any; isNewUser?: boolean }> {
    try {
      // Build Xano API URL from base + API group + branch
      const baseUrl = XANO_HOST;
      const branch = import.meta.env.VITE_XANO_BRANCH || '';
      const branchSuffix = branch ? `:${branch}` : '';
      const exchangeUrl = `${baseUrl}/api:${XANO_API_GROUPS.AUTH}${branchSuffix}/auth/exchange-token`;
      
      // Check if we have pending signup data (name fields)
      const pendingSignup = sessionStorage.getItem('pendingSignup');
      let additionalData = {};
      
      if (pendingSignup) {
        const signupData = JSON.parse(pendingSignup);
        if (signupData.email === stytchResponse.user.emails[0].email) {
          additionalData = {
            first_name: signupData.firstName,
            last_name: signupData.lastName,
            is_new_signup: true
          };
          // Clear the pending data
          sessionStorage.removeItem('pendingSignup');
        }
      }
      
      const payload = {
        stytch_user: stytchResponse.user,
        stytch_session: stytchResponse.session,
        session_token: stytchResponse.session_token,
        session_jwt: stytchResponse.session_jwt,
        is_sso: isSSO,
        ...additionalData
      };
      
      console.log('Attempting token exchange at:', exchangeUrl);
      console.log('Exchange payload:', payload);
      
      const response = await fetch(exchangeUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      
      if (!response.ok) {
        if (response.status === 404) {
          throw new Error('Exchange token endpoint not found. Please implement /auth/exchange-token in Xano.');
        }
        const error = await response.json().catch(() => ({ message: 'Unknown error' }));
        throw new Error(error.message || `Failed to exchange token (${response.status})`);
      }
      
      const data = await response.json();
      console.log('Xano token exchange response:', data);

      // Xano returns { user: {...}, auth: "token", is_new_user: boolean }
      return { token: data.auth, user: data.user, isNewUser: data.is_new_user };
    } catch (error: any) {
      console.error('Token exchange failed:', error);
      throw new Error('Failed to authenticate with backend. Please try again.');
    }
  }
  
  /**
   * Start OAuth flow
   */
  async startOAuthFlow(provider: 'google' | 'microsoft' | 'apple'): Promise<void> {
    try {
      await stytchService.startOAuthFlow(provider);
    } catch (error: any) {
      console.error('Failed to start OAuth flow:', error);
      throw new Error(error.message || 'Failed to start OAuth login');
    }
  }
  
  /**
   * Logout from both systems
   */
  async logout(): Promise<void> {
    try {
      // Revoke Stytch session
      await stytchService.revokeSession();
    } catch (error) {
      console.error('Failed to revoke Stytch session:', error);
    }
    
    // Clear stored tokens
    this.clearSession();
  }
  
  /**
   * Get current session
   */
  getCurrentSession(): DualAuthSession | null {
    const stytchSession = stytchService.getSession();
    const xanoToken = localStorage.getItem('xano_auth_token');
    
    if (!stytchSession || !xanoToken) {
      return null;
    }
    
    return {
      stytchSession: {
        session_id: stytchSession.session_id,
        user_id: stytchSession.user_id,
        expires_at: stytchSession.expires_at,
      },
      xanoToken,
      isAuthenticated: true,
      isLoading: false,
    };
  }
  
  
  /**
   * Map Xano user to our User type
   */
  private mapXanoUserToUser(xanoUser: any, stytchUser?: any): User {
    console.log('mapXanoUserToUser: Raw xanoUser data:', xanoUser);
    // Get role from Xano user using centralized role definitions
    let role: string = USER_ROLES.USER; // Default role
    
    // Use centralized role mapping
    if (xanoUser.user_role) {
      role = getRoleFromId(xanoUser.user_role);
    } else if (xanoUser._user_roles?.role) {
      // Fallback to nested role object if it exists
      role = xanoUser._user_roles.role;
    }
    console.log('mapXanoUserToUser: Mapped role:', role);
    
    return {
      id: xanoUser.id.toString(),
      email: xanoUser.email,
      firstName: xanoUser.first_name || '',
      lastName: xanoUser.last_name || '',
      role: role,
      isActive: true,
      isEmailVerified: stytchUser?.emails[0]?.verified || xanoUser.email_verified || false,
      lastLoginAt: new Date().toISOString(),
      profileImageUrl: null,
      onboarding_completed: xanoUser.onboarding_completed || false,
      subscription_status: xanoUser.subscription_status || 'pending',
      subscription_tier: xanoUser.subscription_tier || null,
      subscription_start_date: xanoUser.subscription_start_date || null,
      subscription_end_date: xanoUser.subscription_end_date || null,
      stripe_customer_id: xanoUser.stripe_customer_id || null,
      stripe_subscription_id: xanoUser.stripe_subscription_id || null,
      is_grandfathered: xanoUser.is_grandfathered || false,
      daily_question_limit: xanoUser.daily_question_limit || 10,
      questions_used_today: xanoUser.questions_used_today || 0,
      last_question_reset: xanoUser.last_question_reset || null,
      active_exam_count: xanoUser.active_exam_count || 0,
      preferences: {
        theme: 'system',
        notifications: {
          email: true,
          push: true,
          reviewAssignments: true,
          budgetWarnings: true,
        },
        timezone: xanoUser.timezone || 'UTC',
        language: 'en',
      },
      createdAt: xanoUser.created_at,
      updatedAt: xanoUser.updated_at || new Date().toISOString(),
    };
  }
  
  /**
   * Parse Stytch user name
   */
  private parseStytchUserName(stytchUser: any): { firstName: string; lastName: string } {
    if (stytchUser.name?.first_name && stytchUser.name?.last_name) {
      return {
        firstName: stytchUser.name.first_name,
        lastName: stytchUser.name.last_name,
      };
    }
    
    // Fallback: try to parse from full name
    const fullName = stytchUser.name || stytchUser.emails[0].email.split('@')[0];
    const parts = fullName.split(' ');
    
    return {
      firstName: parts[0] || '',
      lastName: parts.slice(1).join(' ') || '',
    };
  }
  
  /**
   * Store session tokens
   */
  private storeSession(session: DualAuthSession) {
    // Stytch handles its own session storage
    // Store Xano token
    if (session.xanoToken) {
      localStorage.setItem('xano_auth_token', session.xanoToken);
    }
  }
  
  /**
   * Clear session tokens
   */
  private clearSession() {
    // Stytch handles its own session clearing
    // Clear Xano token
    localStorage.removeItem('xano_auth_token');
    // Clear any cached user data
    sessionStorage.clear();
  }
}

// Export singleton instance
export const dualAuthService = new DualAuthService();